import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RegistryActivityEntry } from '../../api/registryApiTypes';
import { errorMessage } from '../../api/registryApi';
import { useRegistrySession } from '../../session/RegistrySession';

const PAGE_SIZE = 100;

/** Loads the audit trail a page at a time, newest first, and filters what is loaded. */
export function useRegistryActivity(search: string) {
  const session = useRegistrySession()!;
  const [entries, setEntries] = useState<RegistryActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(
    (next: number) => {
      setLoading(true);
      session.api
        .listActivity(next, PAGE_SIZE)
        .then((result) => {
          setEntries((current) => (next === 1 ? result.entries : [...current, ...result.entries]));
          setTotal(result.total);
          setPage(next);
        })
        .catch((failure) => setError(errorMessage(failure)))
        .finally(() => setLoading(false));
    },
    [session],
  );

  useEffect(() => load(1), [load]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((entry) =>
      `${entry.actor} ${entry.action} ${entry.record}`.toLowerCase().includes(term),
    );
  }, [entries, search]);

  return {
    shown,
    remaining: total - entries.length,
    loading,
    error,
    loadOlder: () => load(page + 1),
  };
}
